import React, { createContext, useCallback, useContext, useState } from "react";
import { CargoResponse, getListaCargo } from "../../services/cargoService";
import { getErrorMessage } from "../../locales/translationUtils";
import LoadingOverlay from "../../components/Loading";
import { ApiResponse } from "../../services/api";

interface CargoContextProps {
  cargos: CargoResponse[] | null;
  loading: boolean;
  error: string | null;
  fetchCargos: (filters: any, released?: boolean) => void;
  apiResponse: ApiResponse<CargoResponse> | null;
}

interface CargoProviderProps {
  children: React.ReactNode;
}

const CargoContext = createContext<CargoContextProps | null>(null);

const CargoProvider: React.FC<CargoProviderProps> = ({ children }) => {
  const [cargos, setCargos] = useState<CargoResponse[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [apiResponse, setApiResponse] = useState<ApiResponse<CargoResponse> | null>(null);

  const fetchCargos = useCallback(async (filters: any, released?: boolean) => {
    setError(null);

    try {
      setLoading(true);
      const response = await getListaCargo(filters, released);
      const items: CargoResponse[] = response.data.values;

      setApiResponse(response);
      setCargos(items || []);
    } catch (err) {
      setCargos([]);
      setError(getErrorMessage("fetchCargosError"));
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <CargoContext.Provider value={{ cargos, apiResponse, loading, error, fetchCargos }}>
      <LoadingOverlay isLoading={loading} />
      {children}
    </CargoContext.Provider>
  );
};

export default CargoProvider;

export const useCargo = () => {
  const context = useContext(CargoContext);
  if (!context) {
    throw new Error(
      getErrorMessage("wrongUseProviderError", {
        auth: useCargo?.name,
        providerName: CargoProvider?.name,
      })
    );
  }
  return context;
};
